'use client';

import { ColumnDef } from '@tanstack/react-table';
import { ExternalLink } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DateDisplay } from '@/components/date-display';
import { formatMoney } from "@/lib/money";
import { PaymentStatusBadge } from '@/components/payments/payment-status-badge';
import { PaymentMethodCell, type PaymentMethodInput } from '@/components/payments/payment-method-cell';
import { PaymentResourceBadge } from '@/components/payments/PaymentResourceBadge';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

// Generic payment row used by the embedded order > payments table. Kept
// permissive so it accepts both the OrderDetail['payments'][n] shape and any
// future projection that includes the payment-method input fields.
type PaymentRow = PaymentMethodInput & {
  id: string;
  status: string;
  amount: number;
  currency?: string;
  createdAt: Date;
};

/**
 * Lightweight payment table columns for embedded use in detail pages
 * Focuses on essential information only
 * Works with various payment types (full Payment or nested in OrderDetail)
 */
export function useEmbeddedPaymentsTableColumns(): ColumnDef<PaymentRow>[] {
  const { t } = useTranslation('payments');

  const ActionsCell = ({ paymentId }: { paymentId: string }) => {
    const { orgRoutes } = useOrgNavigate();
    return (
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" asChild>
          <Link to={orgRoutes.PAYMENT_DETAILS(paymentId)}>
            <ExternalLink className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    );
  };

  return React.useMemo<ColumnDef<PaymentRow>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: t('table.columns.paymentId'),
        cell: ({ row }) => {
          const payment = row.original;
          return <PaymentResourceBadge paymentId={payment.id} />;
        },
        size: 180,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('table.columns.status'),
        cell: ({ row }) => {
          const status = row.original.status as any;
          const subStatus = (row.original as any).subStatus;
          return <PaymentStatusBadge status={status} subStatus={subStatus} />;
        },
        size: 120,
      },
      {
        id: 'amount',
        accessorKey: 'amount',
        header: t('table.columns.amount'),
        cell: ({ row }) => {
          const amount = row.original.amount;
          const currency = row.original.currency || 'USD';

          return (
            <div className="flex flex-col gap-0.5">
              <div className="text-sm font-semibold tabular-nums">{formatMoney(amount, currency)}</div>
              {currency !== 'USD' && (
                <div className="text-muted-foreground text-xs font-medium">{currency}</div>
              )}
            </div>
          );
        },
        size: 120,
      },
      {
        id: 'paymentMethod',
        accessorKey: 'paymentInstrument.card',
        header: t('table.columns.paymentMethod'),
        cell: ({ row }) => <PaymentMethodCell payment={row.original} />,
        size: 180,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: t('table.columns.date'),
        cell: ({ row }) => (
          <DateDisplay
            value={row.getValue('createdAt') as string}
            mode="split"
            className="text-sm tabular-nums"
          />
        ),
        size: 130,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const payment = row.original;
          return <ActionsCell paymentId={payment.id} />;
        },
        size: 60,
      },
    ],
    [t],
  );
}
